import React, { ReactNode } from 'react';
import { Button, ButtonProps } from '../../Button';
import { st, classes } from '../MessageBoxFunctionalLayout.st.css';
import { dataHooks } from '../MessageBoxFunctionalLayout.constants';
import {
  MessageBoxFunctionalLayoutProps,
  MessageBoxFunctionalLayoutTheme,
} from '../MessageBoxFunctionalLayout.types';

const buttonSkinByTheme = {
  blue: 'standard',
  purple: 'premium',
  red: 'destructive',
} as const satisfies Record<
  MessageBoxFunctionalLayoutTheme,
  ButtonProps['skin']
>;

const buttonSizeByHeight = {
  'x-small': 'tiny',
  tiny: 'tiny',
  small: 'small',
  medium: 'medium',
  large: 'large',
  'x-large': 'large',
} as const satisfies Record<string, ButtonProps['size']>;

export const FooterLayout = ({
  bottomChildren,
  theme = 'blue',
  cancelText,
  cancelPrefixIcon,
  cancelSuffixIcon,
  onCancel,
  onOk,
  confirmText,
  confirmPrefixIcon,
  confirmSuffixIcon,
  buttonsHeight = 'small',
  enableOk = true,
  enableCancel = true,
  sideActions,
}: Pick<
  MessageBoxFunctionalLayoutProps,
  | 'buttonsHeight'
  | 'confirmText'
  | 'cancelText'
  | 'cancelPrefixIcon'
  | 'cancelSuffixIcon'
  | 'onCancel'
  | 'onOk'
  | 'confirmPrefixIcon'
  | 'confirmSuffixIcon'
  | 'sideActions'
  | 'theme'
> & {
  enableOk: boolean;
  enableCancel: boolean;
  bottomChildren: ReactNode;
}) => (
  <>
    <div className={st(classes.footer)} data-hook={dataHooks.footer}>
      {sideActions}
      <div
        className={st(classes.footerbuttons, {
          withSideActions: sideActions && (cancelText || confirmText),
        })}
      >
        {cancelText && (
          <Button
            prefixIcon={cancelPrefixIcon}
            suffixIcon={cancelSuffixIcon}
            disabled={!enableCancel}
            size={buttonSizeByHeight[buttonsHeight]}
            priority="secondary"
            skin={buttonSkinByTheme[theme]}
            onClick={onCancel}
            dataHook={dataHooks.footerCancelButton}
            children={cancelText}
          />
        )}
        {confirmText && (
          <Button
            prefixIcon={confirmPrefixIcon}
            suffixIcon={confirmSuffixIcon}
            disabled={!enableOk}
            size={buttonSizeByHeight[buttonsHeight]}
            priority="primary"
            skin={buttonSkinByTheme[theme]}
            onClick={onOk}
            dataHook={dataHooks.footerConfirmButton}
            children={confirmText}
          />
        )}
      </div>
    </div>
    {bottomChildren && (
      <div
        data-hook={dataHooks.footerBelowChildren}
        className={st(classes.bottomChildren)}
        children={bottomChildren}
      />
    )}
  </>
);
